import React, { useCallback, useMemo, useState } from "react";
import { GridLegacy, IconButton, TableCell, Tooltip } from "@mui/material";
import FormTextTC from "../../components/Forms/FormTextTC";
import FormV2 from "../JamezzForm/FormV2";
import MainNavbar from "../../components/Navbars/MainNavbar";
import JamezzTableV2, { useReloadState } from "../../components/JamezzTable/V2/JamezzTableV2";
import TranslationWrapper from "../../components/TranslationWrapper/TranslationWrapper";
import { AddCircle, FormatPaint, RemoveCircle } from "@mui/icons-material";
import { _request } from "../../http";
import { Link } from "react-router";
import Edit from "@mui/icons-material/Edit";
import { withStyles } from "tss-react/mui";
import vestigingStyle from "../../assets/jss/material-dashboard-pro-react/views/vestigingStyle";
import FormAsyncTC from "../../components/Forms/FormAsyncTC";
import useJTFilter from "../../components/JamezzTable/V2/useJTFilter";
import AuthProvider from "../../auth/AuthProvider";
import AuthenticationWrapper from "../../auth/AuthenticationWrapper";

const endPoint = "core/profiles";

function ProfileForm({ classes }) {
  const [profile, setProfile] = useState(null);
  const { reloadState, updateReloadState } = useReloadState();
  const defaultFilters1 = useMemo(
    () => ({ "profiles.id": { field: "profiles.id", operator: "=", value: profile?.id } }),
    [profile]
  );
  const [textField1, filters1] = useJTFilter("name", defaultFilters1);
  const defaultFilters2 = useMemo(
    () => ({
      "profiles.id": {
        field: "profiles",
        operator: "or",
        value: [
          { field: "profiles.id", has: false },

          { field: "profiles.id", operator: "=", value: profile?.id, has: false },
        ],
      },
    }),
    [profile]
  );
  const [textField2, filters2] = useJTFilter("name", defaultFilters2);

  const renderHeaderCells = useCallback(
    () => [
      { id: "id", label: "#", numeric: true, disablePadding: false, sortable: true },
      {
        id: "name",
        label: <TranslationWrapper tDefault={"Permissions"} tKey={"permissions.title"} />,
        numeric: false,
        disablePadding: false,
        sortable: true,
      },
    ],
    []
  );

  const renderRowCells = useCallback((row) => {
    return (
      <>
        <TableCell align={"right"} style={{ width: 100 }}>
          {row.id}
        </TableCell>
        <TableCell>{row.name}</TableCell>
      </>
    );
  }, []);

  const renderSelectionActions = useCallback(
    (selected) => {
      return (
        <Tooltip title={<TranslationWrapper tKey={"Remove"} />}>
          <IconButton
            onClick={() => {
              _request(
                "patch",
                `core/profiles/${profile.id}`,
                {
                  roles__id: { detach: Object.keys(selected).map((index) => selected[index]) },
                },
                null
              ).then(() => {
                updateReloadState();
              });
            }}
            style={{ color: "lightcoral" }}
          >
            <RemoveCircle />
          </IconButton>
        </Tooltip>
      );
    },
    [profile]
  );

  const renderSelectionActionsAdd = useCallback(
    (selected) => {
      return (
        <Tooltip title={<TranslationWrapper tKey={"Add"} />}>
          <IconButton
            color={"primary"}
            onClick={() => {
              _request(
                "patch",
                `core/profiles/${profile.id}`,
                {
                  roles__id: { attach: Object.keys(selected).map((index) => selected[index]) },
                },
                null
              ).then(() => {
                updateReloadState();
              });
            }}
          >
            <AddCircle />
          </IconButton>
        </Tooltip>
      );
    },
    [profile]
  );

  const renderRowActions = useCallback((row) => {
    return (
      <TableCell align={"right"}>
        <AuthenticationWrapper permissionKey={`roles/:id/edit`}>
          <Link to={`./${row.id}/edit`}>
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              placement={"bottom"}
              title={<TranslationWrapper tDefault={"Edit"} tKey={"global.jamezzTable.edit"} />}
            >
              <IconButton simple size={"small"} style={{ color: "#07bc0c" }}>
                <Edit />
              </IconButton>
            </Tooltip>
          </Link>
        </AuthenticationWrapper>
      </TableCell>
    );
  }, []);

  return (
    <AuthProvider prefix={"profiles"}>
      <MainNavbar paginaTitel={"Profile"} subject={profile?.name} />
      <GridLegacy container spacing={1}>
        {profile == null || profile?.user_id != null ? (
          <GridLegacy item xs={12}>
            <FormV2
              endPoint={endPoint}
              mode={"onSubmit"}
              onGetModel={(profile) => {
                setProfile(profile);
                if (profile.profile_type) {
                  Object.keys(profile.profile_type).forEach((key) => {
                    profile["profile_type__" + key] = profile.profile_type[key];
                  });
                }
              }}
              revalidateMode={"onChange"}
            >
              <GridLegacy container spacing={1}>
                <FormTextTC
                  name={"name"}
                  permissionKey={profile?.user_id == null ? "global" : "name"}
                  tDefault={"Profile name"}
                  tKey={"profiles.name"}
                />

                <FormTextTC
                  fieldProps={{ disabled: true }}
                  name={"profile_type__name"}
                  tDefault={"Profile type"}
                  tKey={"profiles.profile_type"}
                />
                <FormAsyncTC
                  async={{
                    url: "core/gebruikers/search?fields=id as value, email as label&per_page=50",
                    searchField: "email",
                    extraFilters: [{ field: "master_id", operator: "=", value: null }],
                  }}
                  name={"user_id"}
                  nullable={true}
                  tDefault={"Master user"}
                  tKey={"profiles.user_id"}
                />
              </GridLegacy>
            </FormV2>
          </GridLegacy>
        ) : null}

        <GridLegacy item lg={6} xs={12}>
          {profile ? (
            <>
              {textField1}
              <JamezzTableV2
                checkboxSelection={(content) =>
                  profile?.user_id == null ? (
                    <AuthenticationWrapper permissionKey={"global"}>{content}</AuthenticationWrapper>
                  ) : (
                    content
                  )
                }
                dense={true}
                endPoint={"core/roles"}
                filters={filters1}
                headerIcon={<FormatPaint />}
                reloadState={reloadState}
                renderCellsForRow={renderRowCells}
                renderHeaderCells={renderHeaderCells}
                renderRowActions={renderRowActions}
                renderSelectionActions={renderSelectionActions}
                rowIsEditable={() => false}
                rowIsSelectable={() => true}
                tableTitle={<TranslationWrapper tKey={"User profile is allowed to ..."} />}
                uuid={"ProfileForm-Roles-1oii83i1iomk2"}
              />
            </>
          ) : null}
        </GridLegacy>

        <GridLegacy item lg={6} xs={12}>
          {profile ? (
            <>
              {textField2}
              <JamezzTableV2
                checkboxSelection={(content) =>
                  profile?.user_id == null ? (
                    <AuthenticationWrapper permissionKey={"global"}>{content}</AuthenticationWrapper>
                  ) : (
                    content
                  )
                }
                dense={true}
                endPoint={"core/roles"}
                filters={filters2}
                headerIcon={<FormatPaint />}
                reloadState={reloadState}
                renderCellsForRow={renderRowCells}
                renderHeaderCells={renderHeaderCells}
                renderRowActions={renderRowActions}
                renderSelectionActions={renderSelectionActionsAdd}
                rowIsEditable={() => false}
                rowIsSelectable={() => true}
                tableTitle={<TranslationWrapper tKey={"User profile is not yet allowed to ..."} />}
                uuid={"ProfileForm-p190lmjkhjbn"}
              />
            </>
          ) : null}
        </GridLegacy>
      </GridLegacy>
    </AuthProvider>
  );
}

export default withStyles(ProfileForm, vestigingStyle);
